/**
 * ID-expiration selectable bounds — Flutter parity (PROD-1762).
 *
 * Mirrors `identity_child_widget.dart:381`, which constrains the ID-expiration
 * picker with `firstDate: DateTime.now()` and sets no `lastDate`. Both the
 * patient and the guardian identity field come from that one Flutter widget
 * (it branches on `widget.isGuardian`), so both React call sites take this
 * bound — `identity-verification-section.tsx` and `guardian-section.tsx`.
 *
 * ## Today is selectable; yesterday is not
 *
 * `firstDate: DateTime.now()` carries a time component, which would make "today"
 * unselectable in a naive comparison against a local-midnight cell. Flutter
 * neutralises that explicitly — `field_and_label.dart:3650` subtracts a day
 * inside `selectableDayPredicate` before comparing. So the real semantics are
 * "today or later", stable at any hour.
 *
 * The React side lands on the same semantics for a different reason:
 * `DatePickerField`'s `min` becomes react-day-picker's `disabled: { before }`,
 * which `differenceInCalendarDays` evaluates day-wise rather than by instant.
 * Passing local midnight therefore leaves today enabled. Do NOT "correct" this
 * to tomorrow — an ID expiring today is still valid today.
 *
 * `startOfDay` rather than a bare `new Date()` is deliberate: the DS's
 * today-button check compares raw instants (`if (minDate && t < minDate)`),
 * which is race-prone against a timestamped bound.
 *
 * Injectable `today` so tests need no fake timers — same shape as the
 * co-located `dob-bounds.ts`.
 */

import { startOfDay } from 'date-fns';

/** Earliest selectable ID-expiration date: local midnight today (inclusive). */
export function idExpiryMin(today: Date = new Date()): Date {
  return startOfDay(today);
}
